import { apiClient } from './api-client';
import type { SentimentTrajectory } from '@components/tickets/sentiment/emotionConfig';

// Agent Assist types
export interface AgentAssistRequest {
  ticketId: string;
  subject?: string;
  description?: string;
  customerMessage?: string;
  conversationHistory?: Array<{
    role: 'customer' | 'agent';
    content: string;
    timestamp?: string;
  }>;
  customerId?: string;
  channel?: string;
}

export interface Suggestion {
  id: string;
  type: 'response' | 'action' | 'escalation';
  content: string;
  confidence: number;
  reasoning?: string;
  metadata?: Record<string, unknown>;
}

export interface ArticleReference {
  id: string;
  title: string;
  excerpt: string;
  relevanceScore: number;
  url?: string;
}

export interface SimilarTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  resolution?: string;
  similarity: number;
}

export interface AgentAssistResponse {
  suggestions: Suggestion[];
  articles: ArticleReference[];
  similarTickets?: SimilarTicket[];
  sentiment?: {
    score: number;
    label: 'positive' | 'neutral' | 'negative';
    urgency: 'low' | 'medium' | 'high';
  };
  intent?: {
    primary: string;
    confidence: number;
    secondary?: string[];
  };
}

export interface QuickAnswerRequest {
  question: string;
  ticketId?: string;
  customerId?: string;
  subject?: string;
  context?: string;
}

export interface QuickAnswerResponse {
  answer: string;
  confidence: number;
  sources: ArticleReference[];
}

export interface TypingAssistRequest {
  partialText: string;
  ticketId?: string;
  context?: string;
}

export interface TypingAssistResponse {
  completions: Array<{
    text: string;
    confidence: number;
  }>;
  grammarSuggestions?: Array<{
    original: string;
    suggestion: string;
    reason: string;
  }>;
}

export interface TicketAnalysis {
  summary: string;
  keyPoints: string[];
  sentiment: {
    score: number;
    label: 'positive' | 'neutral' | 'negative';
    trend: 'improving' | 'stable' | 'declining';
  };
  priority: {
    suggested: 'low' | 'medium' | 'high' | 'urgent';
    reason: string;
  };
  category: {
    suggested: string;
    confidence: number;
  };
  estimatedResolutionTime?: string;
  suggestedActions: string[];
  /**
   * Additive emotion-first sentiment lifecycle, derived read-time by the BFF
   * agent-assist `analyzeTicket` from scored customer messages (oldest→newest).
   * Optional — absent when the backend has no per-message sentiment yet.
   */
  sentimentTrajectory?: SentimentTrajectory;
}

export interface CannedResponseSuggestion {
  id: string;
  name: string;
  content: string;
  matchScore: number;
  category?: string;
}

export const agentAssistService = {
  /**
   * Get AI-powered suggestions for a ticket
   */
  async getSuggestions(request: AgentAssistRequest): Promise<AgentAssistResponse> {
    const response = await apiClient.post('/agent-assist/suggestions', request);
    return response.data;
  },

  /**
   * Get a quick answer to a question
   */
  async getQuickAnswer(request: QuickAnswerRequest): Promise<QuickAnswerResponse> {
    const params = new URLSearchParams();
    params.append('question', request.question);
    if (request.ticketId) params.append('ticketId', request.ticketId);
    if (request.customerId) params.append('customerId', request.customerId);
    if (request.subject) params.append('subject', request.subject);
    if (request.context) params.append('context', request.context.slice(0, 300));

    const response = await apiClient.get(`/agent-assist/quick-answer?${params.toString()}`);
    return response.data;
  },

  /**
   * Get typing assistance (autocomplete)
   */
  async getTypingAssist(request: TypingAssistRequest): Promise<TypingAssistResponse> {
    const response = await apiClient.post('/agent-assist/typing-assist', request);
    return response.data;
  },

  /**
   * Analyze a ticket for insights
   */
  async analyzeTicket(ticketId: string, includeHistory = true): Promise<TicketAnalysis> {
    const response = await apiClient.post('/agent-assist/analyze-ticket', {
      ticketId,
      includeHistory,
    });
    return response.data;
  },

  /**
   * Get relevant canned responses
   */
  async getCannedResponses(
    ticketId: string,
    context?: string,
    limit = 5,
  ): Promise<CannedResponseSuggestion[]> {
    const response = await apiClient.post('/agent-assist/canned-responses', {
      ticketId,
      context,
      limit,
    });
    return response.data;
  },
};

export default agentAssistService;
